<template>
    <div class="campaign-moderation-external">
        <div class="campaign-moderation-external__header">
            <h1 class="campaign-moderation-external__title">Модерация группы: {{ displayName }}</h1>
            <button class="update-btn" :class="{ 'update-btn--spinning': syncing }" :disabled="syncing"
                @click="syncStatuses" aria-label="Синхронизировать кампании"
                title="Синхронизировать статусы с API"></button>
        </div>
        <p class="campaign-moderation-external__intro">
            Группа раскрывается на отдельные кампании по одному экрану. Модерируйте и отправляйте каждую кампанию по API
            со своим api_id.
        </p>
        <div class="campaign-moderation-external__list">
            <div v-for="campaign in campaigns" :key="campaign.id" class="moderation-card moderation-card--sub">
                <div class="moderation-card__header">
                    <div class="moderation-card__info">
                        <h3 class="moderation-card__title">
                            <span v-if="campaign.api_id" class="moderation-card__sent-icon"
                                :title="`Отправлено в API (api_id: ${campaign.api_id})`">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#10b981" />
                                    <path d="M8 12.5L11 15.5L16 9.5" stroke="white" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </span>
                            {{ campaign.name }}
                        </h3>
                        <div class="moderation-card__meta">
                            <span>ID: {{ campaign.hierarchical_number || campaign.id }}</span>
                            <span class="moderation-card__status-badge"
                                :class="`moderation-card__status-badge--${campaign.status}`">
                                {{ statusLabel(campaign.status) }}
                            </span>
                            <span>Экранов: {{ campaign.frames_count }}</span>
                            <span>Стоимость: {{ formatCost(campaign.total_cost) }} ₸</span>
                        </div>
                        <div v-if="campaign.advertiser" class="moderation-card__advertiser">
                            <strong>Рекламодатель:</strong> {{ campaign.advertiser.name }}
                        </div>
                        <div v-if="campaign.brand" class="moderation-card__brand">
                            <strong>Бренд:</strong> {{ campaign.brand.name }}
                        </div>
                    </div>
                </div>

                <!-- Смена статуса (только для кампаний с api_id) -->
                <div v-if="campaign.api_id" class="moderation-card__status-change">
                    <label class="moderation-card__status-label">Изменить статус:</label>
                    <select v-model="selectedStatuses[campaign.id]" class="moderation-card__status-select"
                        :disabled="changingStatusId === campaign.id">
                        <option value="">— выберите —</option>
                        <option value="booked">booked</option>
                        <option value="active">active</option>
                        <option value="paused">paused</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                        <option value="rejected">rejected</option>
                    </select>
                    <button v-if="selectedStatuses[campaign.id]" @click="changeStatus(campaign)"
                        class="btn btn--primary btn--sm" :disabled="changingStatusId === campaign.id">
                        {{ changingStatusId === campaign.id ? 'Отправка...' : 'Отправить' }}
                    </button>
                </div>

                <div class="moderation-card__actions">
                    <a :href="`/guaranteed/campaigns/${campaign.id}/moderation`" class="btn btn--primary">
                        Просмотр и редактирование
                    </a>
                </div>
            </div>
        </div>

        <!-- Уведомление о результате -->
        <div v-if="syncMessage" class="sync-notification" :class="syncMessageType">
            {{ syncMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'ModerationExternal',
    props: {
        initialCampaigns: {
            type: Array,
            required: true
        },
        displayName: {
            type: String,
            default: 'Группа кампаний'
        },
        campaignExternalId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            campaigns: [...this.initialCampaigns],
            syncing: false,
            syncMessage: '',
            syncMessageType: '',
            selectedStatuses: {},
            changingStatusId: null
        };
    },
    methods: {
        formatCost(cost) {
            const num = parseFloat(cost) || 0;
            return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        statusLabel(status) {
            const labels = {
                'pending_approval': 'На модерации',
                'approve': 'Approve',
                'reserve': 'Reserve',
                'booked': 'Booked',
                'active': 'Active',
                'activated': 'Activated',
                'paused': 'Paused',
                'completed': 'Completed',
                'cancelled': 'Cancelled',
                'rejected': 'Rejected',
            };
            return labels[status] || status || '—';
        },

        async changeStatus(campaign) {
            const newStatus = this.selectedStatuses[campaign.id];
            if (!newStatus) return;

            this.changingStatusId = campaign.id;
            this.syncMessage = '';

            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const response = await fetch(`/api/campaigns/${campaign.id}/change-status`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ status: newStatus })
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    // Обновляем статус локально
                    const idx = this.campaigns.findIndex(c => c.id === campaign.id);
                    if (idx !== -1) {
                        this.campaigns[idx].status = newStatus;
                    }
                    this.selectedStatuses[campaign.id] = '';
                    this.showNotification(`Статус изменён на "${newStatus}"`, 'success');
                } else {
                    this.showNotification(data.error || 'Ошибка смены статуса', 'error');
                }
            } catch (error) {
                this.showNotification(`Ошибка: ${error.message}`, 'error');
            } finally {
                this.changingStatusId = null;
            }
        },

        async syncStatuses() {
            if (this.syncing) return;
            this.syncing = true;
            this.syncMessage = '';

            try {
                // Шаг 1: Синхронизация статусов с внешним API
                const syncResponse = await this.performStatusSync();

                // Шаг 2: Получение обновлённых данных подкампаний
                const updatedCampaigns = await this.fetchUpdatedCampaigns();

                // Шаг 3: Обновление локальных данных
                const updatedCount = this.updateLocalCampaigns(updatedCampaigns);

                // Шаг 4: Показ уведомления
                this.showSuccessNotification(syncResponse, updatedCount);

            } catch (error) {
                this.handleSyncError(error);
            } finally {
                this.syncing = false;
            }
        },

        async performStatusSync() {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
            const response = await fetch('/api/campaigns/sync-statuses', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Синхронизация не удалась: ${errorText}`);
            }

            return await response.json();
        },

        async fetchUpdatedCampaigns() {
            const response = await fetch(`/api/campaigns/external/${this.campaignExternalId}/campaigns`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Не удалось получить обновлённые данные подкампаний');
            }

            const payload = await response.json();
            return Array.isArray(payload?.campaigns) ? payload.campaigns : [];
        },

        updateLocalCampaigns(updatedCampaigns) {
            const campaignsMap = new Map(
                updatedCampaigns
                    .filter(c => c?.id)
                    .map(c => [String(c.id), c])
            );

            let updatedCount = 0;
            this.campaigns.forEach((campaign, index) => {
                const campaignId = String(campaign.id);
                const freshCampaign = campaignsMap.get(campaignId);

                if (!freshCampaign) return;

                if (freshCampaign.status && freshCampaign.status !== campaign.status) {
                    this.campaigns[index].status = freshCampaign.status;
                    updatedCount++;
                }

                if (freshCampaign.api_id !== campaign.api_id) {
                    this.campaigns[index].api_id = freshCampaign.api_id;
                }
            });

            return updatedCount;
        },

        showNotification(message, type) {
            this.syncMessage = message;
            this.syncMessageType = `sync-notification--${type}`;
            setTimeout(() => { this.syncMessage = ''; }, 5000);
        },

        showSuccessNotification(syncResponse, updatedCount) {
            const campaignsUpdated = syncResponse?.campaigns_updated || 0;
            const totalUpdated = syncResponse?.updated || 0;

            let message = '';
            if (totalUpdated > 0 || updatedCount > 0) {
                const parts = [];
                if (campaignsUpdated > 0) parts.push(`${campaignsUpdated} кампаний`);
                if (updatedCount > 0) parts.push(`${updatedCount} локально`);
                message = `Синхронизация завершена. Обновлено: ${parts.join(', ')}`;
            } else {
                message = 'Синхронизация завершена. Статусы актуальны.';
            }

            this.showNotification(message, 'success');
        },

        handleSyncError(error) {
            this.showNotification(`Ошибка: ${error.message}`, 'error');
        }
    }
};
</script>

<style scoped>
.moderation-card__status-change {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 0;
    border-top: 1px solid var(--card-border, #e2e8f0);
    margin-top: 12px;
}

.moderation-card__status-label {
    font-size: 13px;
    color: var(--muted, #64748b);
    font-weight: 500;
}

.moderation-card__status-select {
    padding: 6px 10px;
    border: 1px solid var(--card-border, #e2e8f0);
    border-radius: 6px;
    font-size: 13px;
    background: #fff;
    min-width: 140px;
}

.moderation-card__status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;
}

.moderation-card__status-badge--pending_approval {
    background: #fef3c7;
    color: #92400e;
}

.moderation-card__status-badge--approve {
    background: #dbeafe;
    color: #1e40af;
}

.moderation-card__status-badge--reserve {
    background: #e0e7ff;
    color: #3730a3;
}

.moderation-card__status-badge--booked {
    background: #d1fae5;
    color: #065f46;
}

.moderation-card__status-badge--active {
    background: #d1fae5;
    color: #065f46;
}

.moderation-card__status-badge--activated {
    background: #d1fae5;
    color: #065f46;
}

.moderation-card__status-badge--paused {
    background: #fef3c7;
    color: #92400e;
}

.moderation-card__status-badge--completed {
    background: #e5e7eb;
    color: #374151;
}

.moderation-card__status-badge--cancelled {
    background: #fee2e2;
    color: #991b1b;
}

.moderation-card__status-badge--rejected {
    background: #fee2e2;
    color: #991b1b;
}

.btn--sm {
    padding: 5px 12px;
    font-size: 13px;
}

.sync-notification {
    position: fixed;
    bottom: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    z-index: 1000;
    animation: slideIn 0.3s ease;
}

.sync-notification--success {
    background: #10b981;
    color: white;
}

.sync-notification--error {
    background: #ef4444;
    color: white;
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>
